import React, { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Select from 'react-select';
import API_BASE_URL from './config/api';
import exportBuildingsToExcel from './buildingIndexExcel';
import './BuildingIndex.css';
import BuildingCompanyChanges from './CompanyChangesPanel';
import { CHANGE_WINDOW_DAYS, changeTime, inWindow, comparisonWindow, loadCompanyHistory } from './companyChangeSummary';
import postalAreas from './data/postalAreas.json';

const INDEX_API = API_BASE_URL.includes('localhost')
  ? 'https://character-ai-app-635021ffeebd.herokuapp.com'
  : API_BASE_URL;

const NOT_LISTED = 'Not listed';
const UNKNOWN_CITY = 'Unknown';
const OFFICE = { lat: 43.64715, lng: -79.40336 };
const DISTANCE_OPTIONS = [5, 10, 15, 25, 50, 100];
const PAGE_SIZE = 250;

// Served from our own public folder so the Excel export can read the bytes;
// the third-party hosts these came from block cross-origin reads.
const LOGO_RULES = [
  { test: /firstservice/i, src: '/management/firstservice.jpg' },
  { test: /i\.?\s*c\.?\s*c\.?|icc property/i, src: '/management/icc.jpg' },
  { test: /del property/i, src: '/management/del.jpg' },
  { test: /duka property/i, src: '/management/duka.jpg' },
  { test: /crossbridge/i, src: '/management/crossbridge.jpg' },
  { test: /icon property/i, src: '/management/icon.jpg' },
  { test: /goldview/i, src: '/management/goldview.jpg' },
  { test: /wilson.*blanchard/i, src: '/management/wilson-blanchard.jpg' },
  { test: /maple ridge/i, src: '/management/maple-ridge.jpg' },
  { test: /stratawest/i, src: '/management/stratawest.jpg' },
  { test: /nadlan-harris/i, src: '/management/nadlan-harris.jpg' },
  { test: /\bassocia\b/i, src: '/management/associa.jpg' },
  { test: /ace condominium/i, src: '/management/ace-black.png' },
  { test: /aa property management/i, src: '/management/aa.png' },
  { test: /andrejs/i, src: '/management/andrejs.png' },
  { test: /royale grande/i, src: '/management/royale-grand.png' },
  { test: /city\s*sites/i, src: '/management/citysites.png' },
];

function companyLabel(name) {
  const trimmed = (name || '').trim();
  return trimmed || NOT_LISTED;
}

function haversineKm(lat1, lng1, lat2, lng2) {
  const toRad = (degrees) => (degrees * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function distanceFromOfficeKm(address) {
  const match = (address || '').toUpperCase().match(/\b([A-Z]\d[A-Z])\s?\d[A-Z]\d\b/);
  const area = match && postalAreas[match[1]];
  if (!area) return null;
  return haversineKm(OFFICE.lat, OFFICE.lng, area[0], area[1]);
}

const DEMO_BUILDINGS = new Set(['123 hudson st', '456 hudson crescent', '789 hudson heights']);

// The same address is keyed and split several times per row; cache both by input string.
const streetKeyCache = new Map();
const addressLinesCache = new Map();

function streetKey(address) {
  const cached = streetKeyCache.get(address);
  if (cached !== undefined) return cached;
  const value = buildStreetKey(address);
  streetKeyCache.set(address, value);
  return value;
}

function buildStreetKey(address) {
  const first = String(address || '').split('\n')[0].split(',')[0];
  return first
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/\./g, '')
    .replace(/\bstreet\b/g, 'st')
    .replace(/\bavenue\b/g, 'ave')
    .replace(/\broad\b/g, 'rd')
    .replace(/\bboulevard\b/g, 'blvd')
    .replace(/\bdrive\b/g, 'dr')
    .replace(/\bcircle\b/g, 'cir')
    .replace(/\bwest\b/g, 'w')
    .replace(/\beast\b/g, 'e')
    .replace(/\bnorth\b/g, 'n')
    .replace(/\bsouth\b/g, 's')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function streetsMatch(left, right) {
  if (!left || !right) return false;
  if (left === right) return true;
  const a = left.split(' ');
  const b = right.split(' ');
  const [shorter, longer] = a.length <= b.length ? [a, b] : [b, a];
  if (shorter.length < 2) return false;
  return shorter.every((token, index) => longer[index] === token);
}

const DEAD_CONTRACT_STATES = ['terminated', 'paused', 'expired'];

function contractRecords(building) {
  return Array.isArray(building.contractData) ? building.contractData : [];
}

function hasActiveContractRecord(building) {
  const name = streetKey(building.name || building.address);
  if (DEMO_BUILDINGS.has(name)) return false;
  if (String(building.status || '').toLowerCase() !== 'active') return false;
  const records = contractRecords(building);
  if (records.length) {
    return records.some((contract) => !DEAD_CONTRACT_STATES.includes(String(contract.status || 'active').toLowerCase()));
  }
  return Array.isArray(building.contracts) && building.contracts.length > 0;
}

// Had Hudson, does not now: worth seeing, and worth seeing that it is gone.
function hasTerminatedContractRecord(building) {
  const name = streetKey(building.name || building.address);
  if (DEMO_BUILDINGS.has(name)) return false;
  const records = contractRecords(building);
  if (!records.length) return false;
  const lost = records.some((contract) => String(contract.status || '').toLowerCase() === 'terminated');
  return lost && !hasActiveContractRecord(building);
}

function cityLabel(city) {
  const trimmed = (city || '').trim();
  if (!trimmed) return UNKNOWN_CITY;
  return trimmed.replace(/\w\S*/g, (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());
}

function logoFor(name) {
  const label = companyLabel(name);
  const rule = LOGO_RULES.find((item) => item.test.test(label));
  return rule ? rule.src : '';
}

function addressLines(address, city) {
  const cacheKey = `${address}|${city}`;
  const cached = addressLinesCache.get(cacheKey);
  if (cached !== undefined) return cached;
  const value = buildAddressLines(address, city);
  addressLinesCache.set(cacheKey, value);
  return value;
}

function buildAddressLines(address, city) {
  const full = (address || '').trim();
  if (!full) return null;
  const parts = full.split(',').map((part) => part.trim()).filter(Boolean);
  const cityName = (city || '').trim().toLowerCase();
  let cityIndex = -1;
  if (cityName) {
    for (let i = parts.length - 1; i >= 1; i -= 1) {
      if (parts[i].toLowerCase() === cityName) {
        cityIndex = i;
        break;
      }
    }
  }
  if (cityIndex < 1) return { street: full, rest: '' };
  return {
    street: `${parts.slice(0, cityIndex).join(', ')},`,
    rest: parts.slice(cityIndex).join(', '),
  };
}

function corpLabel(building) {
  const number = building.number || '';
  if (building.prefix) return `${building.prefix} ${number}`.trim();
  return number || '—';
}

function registryUrl(guid) {
  return guid
    ? `https://www.condoauthorityontario.ca/condo-registry-details/?id=${guid}`
    : '';
}

function searchText(building) {
  return [
    companyLabel(building.company),
    building.managers,
    building.board,
    building.address,
    cityLabel(building.city),
    building.prefix,
    building.number,
    corpLabel(building),
    building.units,
    building.updated,
  ].join(' ').toLowerCase();
}

function infoScore(building) {
  const company = (building.company || '').trim();
  const managers = (building.managers || '').trim();
  const board = (building.board || '').split(';').map((name) => name.trim()).filter(Boolean);
  let score = 0;
  if (company && !/^(self-managed|not listed)$/i.test(company)) score += 5;
  else if (company) score += 1;
  if (managers) score += 5;
  score += Math.min(board.length, 8);
  if (building.units != null && building.units !== '') score += 2;
  if (building.age != null && building.age !== '') score += 1;
  if ((building.city || '').trim()) score += 1;
  if ((building.updated || '').trim()) score += 1;
  return score;
}

function normAddress(address) {
  return (address || '').toLowerCase().replace(/,/g, ' ').split(/\s+/).filter(Boolean).join(' ');
}

function dedupeBuildings(rows) {
  const best = new Map();
  const extras = [];
  rows.forEach((building) => {
    const guid = building.guid;
    if (!guid) {
      extras.push(building);
      return;
    }
    const current = best.get(guid);
    if (!current || infoScore(building) > infoScore(current)) best.set(guid, building);
  });
  return [...best.values(), ...extras];
}

function collapseAddresses(rows) {
  const best = new Map();
  rows.forEach((building) => {
    const key = normAddress(building.address);
    if (!key) return;
    const current = best.get(key);
    if (!current || infoScore(building) > infoScore(current)) best.set(key, building);
  });
  return [...best.values()];
}

function splitNames(value) {
  return (value || '')
    .split(';')
    .map((name) => name.trim())
    .filter(Boolean);
}

const ALBANIAN_GIVEN = new Set([
  'agron', 'albana', 'alban', 'albion', 'alket', 'altin', 'anila', 'anisa', 'arben', 'arber', 'arbri', 'ardian',
  'ardit', 'arjan', 'arjola', 'arlind', 'armend', 'artan', 'astrit', 'besart', 'besim', 'besnik', 'bledar', 'bledi',
  'blerim', 'blerta', 'borana', 'bujar', 'brunilda', 'dalina', 'dardan', 'desantila', 'donika', 'doruntina', 'dritan',
  'edlira', 'eduart', 'eftiola', 'egzon', 'elda', 'elira', 'elona', 'endri', 'endrin', 'endrit', 'enkelejda', 'erblin',
  'ergys', 'erion', 'ermal', 'ervis', 'etleva', 'evis', 'fatmir', 'faton', 'fatos', 'fisnik', 'fjorelo', 'flakim',
  'flamur', 'genc', 'genta', 'gentian', 'genti', 'gerti', 'gjergj', 'gjon', 'granit', 'ilir', 'jetmir', 'jeronim',
  'jonida', 'kastriot', 'kelment', 'kleidi', 'klevis', 'klevisa', 'kliti', 'kujtim', 'ledion', 'ledor', 'lindita',
  'liridon', 'luan', 'luana', 'lulzim', 'majlinda', 'marelba', 'merita', 'mirjeta', 'mirlinda', 'nertila', 'olta',
  'orges', 'orjona', 'pajtim', 'petrit', 'qendrim', 'rajmonda', 'rexhep', 'rezarta', 'rinor', 'shkelqim', 'shpend',
  'shqipe', 'skender', 'skerdi', 'sokol', 'sonila', 'taulant', 'teuta', 'valbona', 'valdet', 'valmira', 'valon',
  'veton', 'violanda', 'violla', 'visar', 'vjola', 'vjosa', 'xhesika', 'xhevahir', 'ylber', 'ylli', 'yllka', 'zamir',
  'zana',
]);

const ALBANIAN_SURNAMES = new Set([
  'agolli', 'ahmeti', 'alimi', 'babatinca', 'bajrami', 'basha', 'bashllari', 'beci', 'beqiri', 'berberi', 'berisha',
  'bojaxhiu', 'buli', 'bytyqi', 'caka', 'celami', 'culi', 'decka', 'deda', 'duka', 'emini', 'fushekati', 'gashi',
  'gega', 'gjonaj', 'gjoka', 'hajdari', 'hali', 'hibeja', 'hoxha', 'hoxhaj', 'hyka', 'hysa', 'kaja', 'kalari',
  'kaloshi', 'kapllani', 'karapici', 'kastrati', 'kelmendi', 'koci', 'kola', 'krasniqi', 'kryeziu', 'kule', 'kumbara',
  'kuqi', 'lako', 'lala', 'lami', 'leka', 'lilollari', 'lleshanaku', 'lleshi', 'marku', 'mehmeti', 'mema', 'miloj',
  'morina', 'mosko', 'murati', 'ndoji', 'nika', 'nikolli', 'pajaziti', 'pano', 'papadhima', 'periku', 'prenga',
  'prifti', 'qallimi', 'qerimi', 'qoshja', 'qosja', 'reci', 'rexhepi', 'rrahmani', 'ruci', 'sadiku', 'salillari',
  'sejdiu', 'selmani', 'shabani', 'shahini', 'shala', 'shehi', 'shehu', 'shkreli', 'spahiu', 'stavri', 'syla',
  'terszana', 'thaci', 'thomai', 'topalli', 'topuzi', 'tota', 'ukshini', 'veseli', 'visha', 'xhakollari', 'xhemajli',
  'ymeri', 'zeneli', 'zeqiri',
]);

const NOT_ALBANIAN_TOKENS = new Set([
  'bajaj', 'bhardwaj', 'chachaj', 'doshi', 'joshi', 'khawaja', 'lalji', 'maharaj', 'raja',
]);

function foldName(token) {
  return token.toLowerCase().replace(/ë/g, 'e').replace(/ç/g, 'c').replace(/[^a-z]/g, '');
}

function albanianToken(raw) {
  const name = foldName(raw);
  if (!name || name.length < 3 || NOT_ALBANIAN_TOKENS.has(name)) return false;
  if (ALBANIAN_GIVEN.has(name) || ALBANIAN_SURNAMES.has(name)) return true;
  if (name.includes('xh')) return true;
  if (name.startsWith('gj') || /gj[aeiou]$/.test(name)) return true;
  if (/gj[aeiou]/.test(name) && !name.includes('ngj') && !/[aeiou]gj/.test(name)) return true;
  const endsAj = name.endsWith('aj') && !name.endsWith('raj') && !name.endsWith('waj');
  const endsAja = name.endsWith('aja') && !name.endsWith('raja') && !name.endsWith('waja');
  if (endsAj || endsAja) return true;
  if (name.endsWith('eu') && !name.endsWith('ieu') && name.length >= 5) return true;
  if (/(eshi|vishi|mishi|xhi|gji)$/.test(name)) return true;
  if (name.length >= 8 && (name.endsWith('llari') || name.endsWith('anaku'))) return true;
  return false;
}

function likelyAlbanianPerson(name) {
  const tokens = name.split(/\s+/).map(foldName).filter(Boolean);
  const polish = tokens.some((token) => /(ska|ski|wicz|czyk|owski|owska)$/.test(token));
  return name.split(/\s+/).some((raw) => {
    if (polish && foldName(raw) === 'kaja') return false;
    return albanianToken(raw);
  });
}

function hasLikelyAlbanianName(building) {
  return [...splitNames(building.managers), ...splitNames(building.board)].some(likelyAlbanianPerson);
}

// Warmest relationships first. Duka scores nothing: the name is known, the welcome is not.
const KNOWN_COMPANIES = [
  { test: /duka property/i, points: 0, name: 'Duka', label: 'No connection' },
  { test: /andrejs|kindle/i, points: 2.4, name: 'Andrejs', label: 'Very strong connection' },
  { test: /del property/i, points: 1.5, name: 'Del', label: 'Very strong connection' },
  { test: /city\s*sites/i, points: 1.1, name: 'City Sites', label: 'Very strong connection' },
  { test: /icon property/i, points: 0.85, name: 'ICON', label: 'Strong connection' },
  { test: /ace condominium/i, points: 0.65, name: 'Ace', label: 'Good connection' },
  { test: /goldview/i, points: 0.45, name: 'Goldview', label: 'Some connection' },
  { test: /firstservice/i, points: 0.25, name: 'FirstService', label: 'Weak connection' },
];

const WARMTH_POINTS_FULL = 4;
const SCORE_MIN = 1;
const SCORE_MAX = 5;

function round1(value) {
  return Math.round(value * 10) / 10;
}

function knownCompany(name) {
  const label = companyLabel(name);
  if (/^(self-managed|not listed)$/i.test(label)) return null;
  const rule = KNOWN_COMPANIES.find((item) => item.test.test(label));
  if (rule) return rule;
  return { points: 0.25, name: label, other: true };
}

function personKey(name) {
  const tokens = String(name || '')
    .toLowerCase()
    .replace(/ë/g, 'e')
    .replace(/ç/g, 'c')
    .replace(/[^a-z\s]/g, ' ')
    .split(/\s+/)
    .filter((token) => token.length > 1 && !/^(mr|mrs|ms|dr|and|the)$/.test(token));
  if (tokens.length < 2) return '';
  return tokens.join(' ');
}

function buildingId(building) {
  return building.guid || normAddress(building.address) || corpLabel(building);
}

// We name some contracts after both doors, like "16 & 18 Harbour St" or "12-16 Yonge St".
// Split those into one key per street number so either door matches the registry address.
function expandStreetKeys(street) {
  const tokens = street.split(' ').filter(Boolean);
  const numbers = [];
  let i = 0;
  while (i < tokens.length) {
    if (/^\d+[a-z]?$/.test(tokens[i])) {
      numbers.push(tokens[i]);
      i += 1;
    } else if (tokens[i] === 'and' && numbers.length && /^\d+[a-z]?$/.test(tokens[i + 1] || '')) {
      i += 1;
    } else {
      break;
    }
  }
  const rest = tokens.slice(i).join(' ');
  if (numbers.length < 2 || !rest) return [street];
  return [street, ...numbers.map((number) => `${number} ${rest}`)];
}

// streetsMatch only ever succeeds when the first two tokens agree, so bucket on those
// instead of comparing every row against every contract address.
function contractStreetIndex(contractStreets) {
  const index = new Map();
  contractStreets.forEach((street) => {
    expandStreetKeys(street).forEach((variant) => {
      const tokens = variant.split(' ');
      if (tokens.length < 2) return;
      const bucket = `${tokens[0]} ${tokens[1]}`;
      if (!index.has(bucket)) index.set(bucket, []);
      index.get(bucket).push(variant);
    });
  });
  return index;
}

function isClientBuilding(building, index) {
  if (!index.size) return false;
  const street = streetKey(addressLines(building.address, building.city)?.street || building.address);
  return expandStreetKeys(street).some((variant) => {
    const tokens = variant.split(' ');
    if (tokens.length < 2) return false;
    const candidates = index.get(`${tokens[0]} ${tokens[1]}`);
    return candidates ? candidates.some((item) => streetsMatch(item, variant)) : false;
  });
}

function fsaOf(address) {
  const match = (address || '').toUpperCase().match(/\b([A-Z]\d[A-Z])\s?\d[A-Z]\d\b/);
  return match ? match[1] : '';
}

// "2287 Lake Shore Blvd W" and "2289 Lake Shore Blvd W" share this key: same street, different door.
function streetNameKey(building) {
  const street = streetKey(addressLines(building.address, building.city)?.street || building.address);
  const name = street.replace(/^[\d\s&-]+/, '').replace(/\s+\d+$/, '').trim();
  return name.split(' ').length >= 2 ? name : '';
}

function streetLabel(building) {
  const place = addressLines(building.address, building.city);
  return (place ? place.street : building.address || '').replace(/,\s*$/, '');
}

function listNames(names) {
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, 2).join(', ')} and ${names.length - 2} more`;
}

function neighbourhoodKey(building) {
  const name = streetNameKey(building);
  if (!name) return '';
  const area = fsaOf(building.address) || cityLabel(building.city);
  return `${area}|${name}`;
}

function ageFit(age) {
  if (age == null || age === '' || Number.isNaN(Number(age))) return null;
  const years = Number(age);
  if (years < 8) return 2.4;
  if (years < 12) return 3.4;
  if (years < 18) return 4.3;
  if (years < 25) return 4.7;
  if (years <= 45) return 5;
  if (years <= 55) return 4.6;
  return 4;
}

// Anywhere in the GTA is a normal drive, so distance barely moves the score until it is far.
function distanceFit(km) {
  if (km == null) return null;
  if (km <= 10) return 5;
  if (km <= 20) return 4.9;
  if (km <= 30) return 4.7;
  if (km <= 40) return 4.5;
  if (km <= 50) return 4.2;
  if (km <= 80) return 3.2;
  return 2.2;
}

// More suites is a bigger prize, so size never counts against a building.
function unitsFit(units) {
  if (units == null || units === '' || Number.isNaN(Number(units))) return null;
  const count = Number(units);
  if (count < 30) return 2.2;
  if (count < 60) return 3;
  if (count < 90) return 3.9;
  if (count < 130) return 4.6;
  return 5;
}

function formatKm(km) {
  if (km == null) return '';
  return km < 10 ? km.toFixed(1) : String(Math.round(km));
}

// A continuous ramp so 5.0, 4.9 and 4.8 never share a colour.
const SCORE_STOPS = [
  [1, [232, 118, 102]],
  [1.8, [240, 152, 92]],
  [2.6, [243, 192, 96]],
  [3.2, [242, 220, 104]],
  [3.8, [216, 226, 108]],
  [4.3, [172, 214, 108]],
  [4.7, [130, 205, 108]],
  [5, [96, 196, 104]],
];

function mix(from, to, ratio) {
  return from.map((channel, index) => Math.round(channel + (to[index] - channel) * ratio));
}

function scoreRgb(value) {
  const score = Math.min(SCORE_MAX, Math.max(SCORE_MIN, Number(value)));
  for (let i = 1; i < SCORE_STOPS.length; i += 1) {
    const [highScore, highRgb] = SCORE_STOPS[i];
    if (score <= highScore) {
      const [lowScore, lowRgb] = SCORE_STOPS[i - 1];
      const span = highScore - lowScore;
      return mix(lowRgb, highRgb, span === 0 ? 0 : (score - lowScore) / span);
    }
  }
  return SCORE_STOPS[SCORE_STOPS.length - 1][1];
}

function rgbText([r, g, b]) {
  return `rgb(${r}, ${g}, ${b})`;
}

function scoreStyle(value) {
  if (value == null || Number.isNaN(Number(value))) {
    return { background: '#EDEFF2', color: '#98A0AB' };
  }
  return { background: rgbText(scoreRgb(value)), color: '#15202e' };
}

function formatScore(value) {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return Number(value).toFixed(1);
}

const CHANGE_LABELS = {
  company: 'Management company',
  managers: 'Manager',
  board: 'Board',
  units: 'Suites',
  age: 'Age',
  city: 'City',
  address: 'Address',
};

const CHANGE_HEADLINES = {
  company: 'New management company',
  managers: 'New manager',
  board: 'New board',
  units: 'Suite count changed',
  age: 'Age updated',
  city: 'City updated',
  address: 'Address updated',
};

export function describeWeeklyCheck(state) {
  const day = (value) => {
    const when = new Date(changeTime(value));
    if (!value || Number.isNaN(when.getTime())) return '';
    return when.toLocaleDateString('en-CA', { weekday: 'long', month: 'short', day: 'numeric', timeZone: 'America/Toronto' });
  };
  if (state.running) {
    const total = state.total ? ` of ${state.total.toLocaleString()}` : '';
    return `Weekly registry check running: ${(state.cursor || 0).toLocaleString()}${total} read, ${state.changed || 0} updated.`;
  }
  const last = state.finishedAt
    ? `Last registry check ${day(state.finishedAt)}: ${(state.checked || 0).toLocaleString()} read, ${state.changed || 0} updated.`
    : 'The registry check has not run yet.';
  const nextTime = new Date(changeTime(state.nextDue));
  const next = Number.isNaN(nextTime.getTime()) ? '' : ` Next ${day(state.nextDue)} at ${nextTime.toLocaleTimeString('en-CA', {hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'America/Toronto'})} Toronto time.`;
  return `${last}${next}`;
}

function nameList(field, value) {
  if (value == null || value === '') return '';
  if (field === 'managers' || field === 'board') return splitNames(String(value)).join(', ');
  return String(value).trim();
}

function describeChange(building, windowEnd, windowStart) {
  if (!building.changedAt) return null;
  const when = new Date(changeTime(building.changedAt));
  if (Number.isNaN(when.getTime())) return null;
  if (!inWindow(building.changedAt, windowStart, windowEnd)) return null;
  const fields = String(building.changedFields || '')
    .split(',')
    .map((field) => field.trim())
    .filter((field) => CHANGE_LABELS[field]);
  if (!fields.length) return null;
  const from = building.changedFrom || {};
  const day = when.toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });
  return {
    fields,
    when: day,
    headline: fields.length === 1
      ? CHANGE_HEADLINES[fields[0]]
      : `${fields.length} registry changes`,
    lines: fields.map((field) => ({
      field,
      label: CHANGE_LABELS[field],
      was: nameList(field, from[field]),
      now: nameList(field, building[field]),
    })),
  };
}

function scoreOne(building, ctx) {
  const id = buildingId(building);
  const client = ctx.clientIds.has(id);
  const managers = splitNames(building.managers);
  const board = splitNames(building.board);
  const managerKeys = managers.map((name) => ({ name, key: personKey(name) })).filter((item) => item.key);
  const boardKeys = board.map((name) => ({ name, key: personKey(name) })).filter((item) => item.key);
  const managerHit = managerKeys.find((item) => ctx.managerClients.has(item.key));
  const boardHit = boardKeys.find((item) => ctx.managerClients.has(item.key) || ctx.boardClients.has(item.key));
  const albanianManager = managers.find(likelyAlbanianPerson) || '';
  const albanianBoard = albanianManager ? '' : (board.find(likelyAlbanianPerson) || '');
  const known = knownCompany(building.company);
  const warmest = known && known.points >= 1.5;
  const reasons = [];
  let points = 0;

  if (client) reasons.push('Already a Hudson client');
  else if (ctx.terminatedIds.has(id)) reasons.push('We had this building and lost it');

  const focus = managerHit || managerKeys[0];
  const group = focus ? (ctx.byManager.get(focus.key) || []) : [];
  const openCount = group.filter((item) => buildingId(item) !== id && !ctx.clientIds.has(buildingId(item))).length;

  if (managerHit) {
    const info = ctx.managerClients.get(managerHit.key);
    const others = Math.max(0, info.count - (client ? 1 : 0));
    points += 2.4 + Math.min(0.6, 0.2 * (info.count - 1));
    if (!client) {
      reasons.push(`${managerHit.name} already manages ${info.count} Hudson building${info.count === 1 ? '' : 's'}`);
    } else if (others > 0) {
      reasons.push(`This manager also runs ${others} other Hudson building${others === 1 ? '' : 's'}`);
    }
    if (!client && openCount >= 2) {
      points += 0.35;
      reasons.push(`${openCount} other buildings with this manager are still open`);
    }
  } else if (boardHit) {
    points += 1.3;
    reasons.push(`${boardHit.name} already sits on a Hudson board`);
  }

  if (albanianManager) {
    points += warmest ? 2.6 : 2;
    reasons.push(`1st-degree manager · ${albanianManager}`);
  } else if (albanianBoard) {
    points += warmest ? 1.2 : 0.9;
    reasons.push(`1st-degree board member · ${albanianBoard}`);
  }

  if (known) {
    points += known.points;
    if (!known.other) reasons.push(`${known.name} · ${known.label}`);
  }

  // A Hudson building next door only helps much when the same company runs both.
  const companyName = companyLabel(building.company);
  const neighbours = ctx.clientNeighbours.get(neighbourhoodKey(building));
  if (!client && neighbours) {
    const sameCompany = neighbours.companies.has(companyName);
    points += sameCompany ? 2 : 0.7;
    reasons.push(sameCompany
      ? `Next door to ${listNames(neighbours.names)}, same company`
      : `Next door to ${listNames(neighbours.names)}, but a different company`);
  }
  const area = fsaOf(building.address);
  const nearby = area ? ctx.clientAreas.get(area) : null;
  if (!client && !neighbours && nearby) {
    const sameCompany = nearby.companies.has(companyName);
    points += sameCompany ? 0.6 + Math.min(0.5, 0.12 * nearby.count) : 0.25;
    const sample = nearby.names.slice(0, 2).join(', ');
    reasons.push(`${nearby.count} Hudson building${nearby.count === 1 ? '' : 's'} already in ${area}${sameCompany ? ', same company' : ''} · ${sample}${nearby.count > 2 ? ' and more' : ''}`);
  }

  const warmth = round1(SCORE_MIN + (SCORE_MAX - SCORE_MIN) * Math.min(1, points / WARMTH_POINTS_FULL));
  const km = distanceFromOfficeKm(building.address);
  // Age matters most, then size, then the drive. Weights renormalise when a field is blank.
  const parts = [
    [ageFit(building.age), 0.45],
    [unitsFit(building.units), 0.3],
    [distanceFit(km), 0.25],
  ].filter(([value]) => value != null);
  const weight = parts.reduce((sum, [, share]) => sum + share, 0);
  const fit = weight ? round1(parts.reduce((sum, [value, share]) => sum + value * share, 0) / weight) : null;
  const fitNotes = [];
  if (building.age != null && building.age !== '' && !Number.isNaN(Number(building.age))) {
    const years = Number(building.age);
    let note = `Age ${years}`;
    if (years >= 25 && years <= 45) note += ' · fan-coil window';
    else if (years >= 12 && years < 25) note += ' · coming due';
    else if (years < 12) note += ' · still new';
    else note += ' · past the usual window';
    fitNotes.push(note);
  }
  if (km != null) fitNotes.push(`${formatKm(km)} km from the office`);
  if (building.units != null && building.units !== '' && !Number.isNaN(Number(building.units))) {
    fitNotes.push(`${Number(building.units).toLocaleString()} voting units`);
  }

  return {
    client,
    terminated: ctx.terminatedIds.has(id),
    warmth,
    fit,
    overall: fit == null ? warmth : round1(warmth * 0.62 + fit * 0.38),
    reasons,
    fitNotes,
    warmthTip: reasons.length ? reasons.join('\n') : 'No connection yet: cold call',
    fitTip: fitNotes.length ? fitNotes.join('\n') : 'Age, distance and unit count not listed',
    km,
    openCount,
    managerKey: focus ? focus.key : '',
  };
}

function rankBuildings(rows, contractStreets, terminatedStreets, windowEnd, windowStart) {
  const clientIds = new Set();
  const terminatedIds = new Set();
  const managerClients = new Map();
  const boardClients = new Map();
  const byManager = new Map();
  const clientNeighbours = new Map();
  const clientAreas = new Map();
  const index = contractStreetIndex(contractStreets);
  const lostIndex = contractStreetIndex(terminatedStreets);

  rows.forEach((building) => {
    const id = buildingId(building);
    if (isClientBuilding(building, index)) clientIds.add(id);
    else if (isClientBuilding(building, lostIndex)) terminatedIds.add(id);
  });

  const bump = (map, key, name, company) => {
    if (!key) return;
    const current = map.get(key) || { count: 0, names: [], companies: new Set() };
    current.count += 1;
    if (name && !current.names.includes(name)) current.names.push(name);
    if (company) current.companies.add(company);
    map.set(key, current);
  };

  rows.forEach((building) => {
    const id = buildingId(building);
    const isClient = clientIds.has(id);
    splitNames(building.managers).forEach((name) => {
      const key = personKey(name);
      if (!key) return;
      if (!byManager.has(key)) byManager.set(key, []);
      byManager.get(key).push(building);
      if (!isClient) return;
      const current = managerClients.get(key) || { name, count: 0 };
      current.count += 1;
      managerClients.set(key, current);
    });
    if (!isClient) return;
    const label = streetLabel(building);
    const company = companyLabel(building.company);
    bump(clientNeighbours, neighbourhoodKey(building), label, company);
    bump(clientAreas, fsaOf(building.address), label, company);
    splitNames(building.board).forEach((name) => {
      const key = personKey(name);
      if (!key) return;
      const current = boardClients.get(key) || { name, count: 0 };
      current.count += 1;
      boardClients.set(key, current);
    });
  });

  const ctx = { clientIds, terminatedIds, managerClients, boardClients, byManager, clientNeighbours, clientAreas };
  const ranked = rows.map((building) => ({
    ...building,
    rank: scoreOne(building, ctx),
    // Built once here so filtering and sorting 6,000 rows stays cheap. Plain lowercase
    // keys let the comparators use < and > instead of localeCompare.
    haystack: searchText(building),
    place: addressLines(building.address, building.city),
    managerList: splitNames(building.managers).join(', '),
    boardList: splitNames(building.board).join(', '),
    sortAddress: (building.address || '').toLowerCase(),
    sortCorp: `${(building.prefix || '').toLowerCase()} ${String(building.number || '').padStart(7, '0')}`,
    sortCompany: companyLabel(building.company).toLowerCase(),
    sortManager: (building.managers || '').toLowerCase(),
    sortBoard: (building.board || '').toLowerCase(),
    change: describeChange(building, windowEnd, windowStart),
  }));
  // Sort each manager's portfolio once, not once per building in it.
  const byId = new Map(ranked.map((building) => [buildingId(building), building]));
  const sortedGroups = new Map();
  byManager.forEach((group, key) => {
    const sorted = group
      .map((item) => byId.get(buildingId(item)))
      .filter(Boolean)
      .sort((a, b) => {
        if (a.rank.client !== b.rank.client) return a.rank.client ? -1 : 1;
        const left = a.address || '';
        const right = b.address || '';
        return left < right ? -1 : left > right ? 1 : 0;
      });
    sortedGroups.set(key, sorted);
  });

  ranked.forEach((building) => {
    const group = sortedGroups.get(building.rank.managerKey);
    if (!group || group.length < 2) {
      building.rank.siblings = [];
      building.rank.siblingCount = 0;
      return;
    }
    const id = buildingId(building);
    const siblings = [];
    for (let i = 0; i < group.length && siblings.length < 8; i += 1) {
      if (buildingId(group[i]) !== id) siblings.push(group[i]);
    }
    building.rank.siblings = siblings;
    building.rank.siblingCount = group.length - 1;
  });
  return ranked;
}

function scoreValue(building, key) {
  const rank = building.rank || {};
  const value = rank[key];
  return value == null || Number.isNaN(Number(value)) ? null : Number(value);
}

function ScoreCell({ value, size = 'row' }) {
  return (
    <span
      className={`building-index__score-box building-index__score-box--${size}`}
      style={scoreStyle(value)}
    >
      {formatScore(value)}
    </span>
  );
}

function RangeFilter({ label, value, onChange }) {
  const [low, high] = value;
  const touched = low > SCORE_MIN || high < SCORE_MAX;
  const pct = (score) => ((score - SCORE_MIN) / (SCORE_MAX - SCORE_MIN)) * 100;
  const setLow = (next) => onChange([Math.min(next, high), high]);
  const setHigh = (next) => onChange([low, Math.max(next, low)]);

  return (
    <div className={`building-index__range${touched ? ' building-index__range--on' : ''}`}>
      <div className="building-index__range-head">
        <span>{formatScore(low)}</span>
        <strong>{label}</strong>
        <span>{formatScore(high)}</span>
      </div>
      <div className="building-index__range-track">
        <span className="building-index__range-rail" />
        <span
          className="building-index__range-fill"
          style={{ left: `${pct(low)}%`, right: `${100 - pct(high)}%` }}
        />
        <input
          type="range"
          min={SCORE_MIN}
          max={SCORE_MAX}
          step="0.1"
          value={low}
          aria-label={`Minimum ${label}`}
          onChange={(event) => setLow(Number(event.target.value))}
        />
        <input
          type="range"
          min={SCORE_MIN}
          max={SCORE_MAX}
          step="0.1"
          value={high}
          aria-label={`Maximum ${label}`}
          onChange={(event) => setHigh(Number(event.target.value))}
        />
      </div>
    </div>
  );
}

function optionList(values) {
  return [...new Set(values)]
    .sort((a, b) => a.localeCompare(b))
    .map((value) => ({ value, label: value }));
}

const SORT_COLUMNS = [
  { key: 'pick', label: '', width: 30, action: true },
  { key: 'company', label: 'Mgmt', width: 86 },
  { key: 'corp', label: 'Corp', width: 80 },
  { key: 'building', label: 'Address', width: 272 },
  {
    key: 'overall',
    label: 'Overall',
    score: true,
    tip: 'Who to call first. Warmth counts a little more than Fit. Buildings we already have are shaded green, and lost ones red.',
  },
  {
    key: 'warmth',
    label: 'Warmth',
    score: true,
    tip: 'How easy the introduction is. Highest when the manager already runs a Hudson building, or the building sits on the same street as one. Then a board member we know, a 1st-degree name, and the company: Andrejs first because we know the owner, then Del, City Sites, ICON, Ace, Goldview, then FirstService and the rest. Duka adds nothing.',
  },
  {
    key: 'fit',
    label: 'Fit',
    score: true,
    tip: 'Whether the building is worth the work. Age counts most, best from 25 to 45 years for fan coils and still strong from 12. Then size, where more suites is always better. Then the drive, which barely matters inside the GTA.',
  },
  { key: 'manager', label: 'Manager', width: 156 },
  { key: 'age', label: 'Age', width: 56 },
  { key: 'units', label: 'Units', width: 64 },
  { key: 'board', label: 'Board', width: 212 },
];

const CUSTOM_LIST_KEY = 'buildingIndexCustomList';

function readCustomList() {
  try {
    const stored = JSON.parse(localStorage.getItem(CUSTOM_LIST_KEY));
    return new Set(Array.isArray(stored) ? stored : []);
  } catch (error) {
    return new Set();
  }
}

const SCORE_COLUMN_WIDTH = 66;

function textValue(value) {
  return String(value || '').trim();
}

function compareKey(a, b) {
  if (a === b) return 0;
  return a < b ? -1 : 1;
}

function compareBuilding(a, b) {
  const corp = compareKey(a.sortCorp, b.sortCorp);
  if (corp) return corp;
  return compareKey(a.sortAddress, b.sortAddress);
}

function compareNumber(a, b) {
  const aMissing = a == null || Number.isNaN(Number(a));
  const bMissing = b == null || Number.isNaN(Number(b));
  if (aMissing && bMissing) return 0;
  if (aMissing) return 1;
  if (bMissing) return -1;
  return Number(a) - Number(b);
}

function compareByColumn(a, b, key) {
  if (key === 'company') return compareKey(a.sortCompany, b.sortCompany);
  if (key === 'corp') return compareBuilding(a, b);
  if (key === 'manager') return compareKey(a.sortManager, b.sortManager);
  if (key === 'board') return compareKey(a.sortBoard, b.sortBoard);
  if (key === 'overall' || key === 'warmth' || key === 'fit') return compareNumber(scoreValue(a, key), scoreValue(b, key));
  if (key === 'age' || key === 'units') return compareNumber(a[key], b[key]);
  return compareBuilding(a, b);
}

function isBlankForSort(building, key) {
  if (key === 'overall' || key === 'warmth' || key === 'fit') return scoreValue(building, key) == null;
  if (key === 'age' || key === 'units') return building[key] == null || Number.isNaN(Number(building[key]));
  if (key === 'company') return !textValue(building.company);
  if (key === 'manager') return !textValue(building.managers);
  if (key === 'board') return !textValue(building.board);
  if (key === 'corp') return !textValue(building.prefix) && !textValue(building.number);
  return !textValue(building.address);
}

function SortMark({ direction }) {
  const up = direction === 'asc' ? '#1F3A68' : '#C5CAD3';
  const down = direction === 'desc' ? '#1F3A68' : '#C5CAD3';
  return (
    <svg className="building-index__sort-mark" width="10" height="12" viewBox="0 0 10 12" aria-hidden>
      <path d="M5 0.5 L9.2 5 H0.8 Z" fill={up} />
      <path d="M5 11.5 L0.8 7 H9.2 Z" fill={down} />
    </svg>
  );
}

const BuildingRow = React.memo(function BuildingRow({ building, selected, picked, onSelect, onPick }) {
  const company = companyLabel(building.company);
  const logo = logoFor(company);
  const { place, rank } = building;
  const street = place ? place.street.replace(/,\s*$/, '') : '';
  const open = () => onSelect(building);

  return (
    <tr
      className={[
        rank.client ? 'building-index__contract' : '',
        rank.terminated ? 'building-index__lost' : '',
        selected ? 'building-index__selected' : '',
      ].filter(Boolean).join(' ') || undefined}
      onClick={open}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          open();
        }
      }}
      tabIndex={0}
    >
      <td className="building-index__pick-cell">
        <button
          type="button"
          className={`building-index__pick${picked ? ' building-index__pick--on' : ''}`}
          onClick={(event) => {
            event.stopPropagation();
            onPick(building);
          }}
          title={picked ? 'Remove from custom list' : 'Add to custom list'}
          aria-label={picked ? 'Remove from custom list' : 'Add to custom list'}
        >
          <span aria-hidden>{picked ? '−' : '+'}</span>
        </button>
      </td>
      <td>
        <div className="building-index__logo" title={company}>
          {logo ? (
            <img src={logo} alt={company} loading="lazy" decoding="async" />
          ) : (
            <span className="building-index__company-name">{company}</span>
          )}
        </div>
      </td>
      <td className="building-index__corp">{corpLabel(building)}</td>
      <td className="building-index__address" title={building.address || ''}>
        <span className="building-index__clip">
          {building.change && (
            <span
              className="building-index__dot"
              title={`${building.change.headline} · ${building.change.when}\n${building.change.lines.map((line) => `${line.label}: ${line.was || '—'} → ${line.now || '—'}`).join('\n')}`}
            >
              ●
            </span>
          )}
          {street ? <strong>{street}</strong> : '—'}
          {place?.rest ? <span className="building-index__locality">{`, ${place.rest}`}</span> : null}
        </span>
      </td>
      <td className="building-index__score" title={`${rank.warmthTip}\n\n${rank.fitTip}`}>
        <ScoreCell value={rank.overall} />
      </td>
      <td className="building-index__score" title={rank.warmthTip}>
        <ScoreCell value={rank.warmth} />
      </td>
      <td className="building-index__score" title={rank.fitTip}>
        <ScoreCell value={rank.fit} />
      </td>
      <td className="building-index__clip-cell" title={building.managerList}>
        <span className="building-index__clip building-index__clip--person">{building.managerList || '—'}</span>
      </td>
      <td className="building-index__num">{building.age == null ? '—' : building.age}</td>
      <td className="building-index__num">{building.units == null ? '—' : building.units.toLocaleString()}</td>
      <td className="building-index__clip-cell" title={building.boardList}>
        <span className="building-index__clip building-index__clip--board">{building.boardList || '—'}</span>
      </td>
    </tr>
  );
});

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 40,
    borderRadius: 8,
    borderColor: state.isFocused ? '#1F3A68' : '#E6E8EC',
    boxShadow: 'none',
    fontSize: 14,
    ':hover': { borderColor: '#1F3A68' },
  }),
  menu: (base) => ({ ...base, zIndex: 20, fontSize: 14 }),
  placeholder: (base) => ({ ...base, color: '#9CA3AF' }),
  multiValue: (base) => ({ ...base, background: '#EFF2F7', borderRadius: 4 }),
  multiValueLabel: (base) => ({ ...base, color: '#1a2236', fontSize: 12 }),
};

export default function BuildingIndex() {
  const [companies, setCompanies] = useState([]);
  const [cities, setCities] = useState([]);
  const [ageMin, setAgeMin] = useState('');
  const [maxKm, setMaxKm] = useState('');
  const [connection, setConnection] = useState('');
  const [overallRange, setOverallRange] = useState([SCORE_MIN, SCORE_MAX]);
  const [warmthRange, setWarmthRange] = useState([SCORE_MIN, SCORE_MAX]);
  const [fitRange, setFitRange] = useState([SCORE_MIN, SCORE_MAX]);
  const [query, setQuery] = useState('');
  const [sortKey, setSortKey] = useState('overall');
  const [sortDir, setSortDir] = useState('desc');
  const [selected, setSelected] = useState(null);
  const [buildings, setBuildings] = useState([]);
  const [progress, setProgress] = useState(null);
  const [loadState, setLoadState] = useState('loading');
  const [contractStreets, setContractStreets] = useState([]);
  const [terminatedStreets, setTerminatedStreets] = useState([]);
  const [showActive, setShowActive] = useState(true);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [customIds, setCustomIds] = useState(readCustomList);
  const [showCustomOnly, setShowCustomOnly] = useState(false);
  const [exportState, setExportState] = useState('');
  const [showChangedOnly, setShowChangedOnly] = useState(false);
  const [refresh, setRefresh] = useState(null);
  const [refreshAsk, setRefreshAsk] = useState('');
  const [history, setHistory] = useState([]);
  const [historyStatus, setHistoryStatus] = useState('loading');
  const [historyRetry, setHistoryRetry] = useState(0);
  const [windowEnd, setWindowEnd] = useState(Date.now);
  const [windowStart, setWindowStart] = useState(() => Date.now() - CHANGE_WINDOW_DAYS * 86400000);
  const [historyMode, setHistoryMode] = useState('latest');
  const [runInfo, setRunInfo] = useState(null);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const sentinelRef = useRef(null);
  const signatureRef = useRef('');

  // Typing and dragging stay responsive; the 6,000 row pass runs just behind them.
  const deferredQuery = useDeferredValue(query);
  const deferredOverall = useDeferredValue(overallRange);
  const deferredWarmth = useDeferredValue(warmthRange);
  const deferredFit = useDeferredValue(fitRange);

  useEffect(() => {
    let stop = false;
    const load = async () => {
      const token = localStorage.getItem('managerToken');
      if (!token) {
        if (!stop) setLoadState('error');
        return;
      }
      try {
        // Both at once, then one state update, so the ranking pass runs a single time.
        const [response, contractResponse, refreshResponse, runsResponse] = await Promise.all([
          fetch(`${INDEX_API}/api/discovery/buildings`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${INDEX_API}/api/buildings`).catch(() => null),
          fetch(`${INDEX_API}/api/discovery/refresh`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => null),
          fetch(`${INDEX_API}/api/discovery/runs`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => null),
        ]);
        const runs = runsResponse?.ok ? await runsResponse.json() : {comparisonAvailable: false, reason: 'Completed-check history is unavailable. A preceding completed baseline is required.'};
        if (!stop) setRunInfo(runs);
        let registryVersion = '';
        if (refreshResponse && refreshResponse.ok) {
          const state = await refreshResponse.json();
          registryVersion = `${state.finishedAt || ''}|${state.cursor || ''}|${state.changed || ''}`;
          if (!stop) setRefresh(state);
        }
        if (!response.ok) {
          if (!stop) setLoadState((current) => (current === 'ready' ? 'ready' : 'error'));
          return;
        }
        const data = await response.json();
        if (stop || !Array.isArray(data.buildings)) return;

        let streets = null;
        let lost = null;
        if (contractResponse && contractResponse.ok) {
          const contractData = await contractResponse.json();
          const list = Array.isArray(contractData.buildings) ? contractData.buildings : [];
          const toStreet = (item) => streetKey(item.address || item.name);
          streets = list.filter(hasActiveContractRecord).map(toStreet).filter(Boolean);
          lost = list.filter(hasTerminatedContractRecord).map(toStreet).filter(Boolean);
        }
        if (stop) return;

        // Skip the work when the poll brings back the same crawl state.
        const signature = `${data.buildings.length}|${data.progress?.hits || ''}|${data.progress?.label || ''}|${registryVersion}|${streets ? streets.length : 'none'}|${lost ? lost.length : 'none'}`;
        if (signature !== signatureRef.current) {
          signatureRef.current = signature;
          setBuildings(data.buildings);
          setProgress(data.progress || null);
          if (streets) setContractStreets(streets);
          if (lost) setTerminatedStreets(lost);
        }
        setLoadState('ready');
      } catch (error) {
        if (!stop) setLoadState((current) => (current === 'ready' ? 'ready' : 'error'));
      }
    };
    load();
    // Five minutes, not one: each refresh re-scores every row, and the crawl count
    // ticking over is not worth a pause in the table.
    const timer = setInterval(load, 300000);
    return () => {
      stop = true;
      clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    if (!showChangedOnly) return undefined;
    const controller = new AbortController();
    const token = localStorage.getItem('managerToken');
    const window = comparisonWindow(runInfo, historyMode);
    setWindowEnd(Number.isFinite(window.end) ? window.end : Date.now());
    setWindowStart(Number.isFinite(window.start) ? window.start : Date.now());
    if (historyMode === 'latest' && !runInfo?.comparisonAvailable) {
      setHistory([]);
      setHistoryStatus('unavailable');
      return () => controller.abort();
    }
    setHistoryStatus('loading');
    loadCompanyHistory(INDEX_API, token, window.end, controller.signal, window)
      .then((rows) => {
        if (!controller.signal.aborted) {
          setHistory(rows);
          setHistoryStatus('ready');
        }
      })
      .catch(() => {
        if (!controller.signal.aborted) setHistoryStatus('error');
      });
    return () => controller.abort();
  }, [showChangedOnly, historyRetry, historyMode, runInfo]);

  const companyOptions = useMemo(
    () => optionList(buildings.map((building) => companyLabel(building.company))),
    [buildings]
  );
  const cityOptions = useMemo(
    () => optionList(buildings.map((building) => cityLabel(building.city))),
    [buildings]
  );

  const ranked = useMemo(
    () => rankBuildings(collapseAddresses(dedupeBuildings(buildings)), contractStreets, terminatedStreets, windowEnd, windowStart),
    [buildings, contractStreets, terminatedStreets, windowEnd, windowStart]
  );

  // Null unless the custom view is on, so picking a row does not re-run the whole pass.
  const customFilter = showCustomOnly ? customIds : null;

  const filtered = useMemo(() => {
    const companySet = new Set(companies.map((option) => option.value));
    const citySet = new Set(cities.map((option) => option.value));
    const min = ageMin === '' ? null : Number(ageMin);
    const maxDistance = maxKm === '' ? null : Number(maxKm);
    const needle = deferredQuery.trim().toLowerCase();
    const terms = needle.split(/\s+/).filter(Boolean);

    const inRange = (score, [low, high]) => {
      if (low <= SCORE_MIN && high >= SCORE_MAX) return true;
      if (score == null) return false;
      return score >= low && score <= high;
    };

    return ranked
      .filter((building) => {
        if (customFilter && !customFilter.has(buildingId(building))) return false;
        if (showChangedOnly && !building.change) return false;
        if (!showActive && building.rank.client) return false;
        if (connection === '1st' && !hasLikelyAlbanianName(building)) return false;
        if (!inRange(building.rank.overall, deferredOverall)) return false;
        if (!inRange(building.rank.warmth, deferredWarmth)) return false;
        if (!inRange(building.rank.fit, deferredFit)) return false;
        if (terms.length && !terms.every((term) => building.haystack.includes(term))) return false;
        if (companySet.size && !companySet.has(companyLabel(building.company))) return false;
        if (citySet.size && !citySet.has(cityLabel(building.city))) return false;
        if (min !== null) {
          if (building.age == null || Number.isNaN(building.age) || building.age < min) return false;
        }
        if (maxDistance !== null) {
          const km = building.rank.km;
          if (km == null || km > maxDistance) return false;
        }
        return true;
      })
      .slice()
      .sort((a, b) => {
        const aBlank = isBlankForSort(a, sortKey);
        const bBlank = isBlankForSort(b, sortKey);
        if (aBlank !== bBlank) return aBlank ? 1 : -1;
        const primary = compareByColumn(a, b, sortKey);
        const directed = sortDir === 'desc' ? -primary : primary;
        if (directed) return directed;
        return compareBuilding(a, b);
      });
  }, [ranked, companies, cities, ageMin, maxKm, connection, showActive, showChangedOnly, customFilter, deferredOverall, deferredWarmth, deferredFit, deferredQuery, sortKey, sortDir]);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [companies, cities, ageMin, maxKm, connection, showActive, showChangedOnly, showCustomOnly, deferredOverall, deferredWarmth, deferredFit, deferredQuery, sortKey, sortDir]);

  useEffect(() => {
    try {
      localStorage.setItem(CUSTOM_LIST_KEY, JSON.stringify([...customIds]));
    } catch (error) {
      // A full or blocked store only costs us the list next visit.
    }
  }, [customIds]);

  // An empty custom list has nothing to show, so drop back to the full table.
  useEffect(() => {
    if (customIds.size === 0) setShowCustomOnly(false);
  }, [customIds]);

  const togglePick = useCallback((building) => {
    const id = buildingId(building);
    setCustomIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  useEffect(() => {
    if (!selected) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') setSelected(null);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [selected]);

  const visibleBuildings = filtered.slice(0, visibleCount);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || visibleCount >= filtered.length) return undefined;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        setVisibleCount((count) => Math.min(count + PAGE_SIZE, filtered.length));
      }
    }, { rootMargin: '600px' });
    observer.observe(node);
    return () => observer.disconnect();
  }, [visibleCount, filtered.length]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((dir) => (dir === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortKey(key);
    setSortDir(key === 'overall' || key === 'warmth' || key === 'fit' ? 'desc' : 'asc');
  };

  // Everything the reader would otherwise have to guess about how the list was cut.
  const describeFilters = () => {
    const parts = [];
    if (showCustomOnly) parts.push('custom list');
    if (companies.length) parts.push(`management: ${companies.map((option) => option.label).join(', ')}`);
    if (cities.length) parts.push(`city: ${cities.map((option) => option.label).join(', ')}`);
    if (ageMin !== '') parts.push(`${ageMin}+ years old`);
    if (maxKm !== '') parts.push(`within ${maxKm} km`);
    if (connection === '1st') parts.push('1st degree names');
    const range = (label, [low, high]) => {
      if (low <= SCORE_MIN && high >= SCORE_MAX) return;
      parts.push(`${label} ${low.toFixed(1)}–${high.toFixed(1)}`);
    };
    range('overall', overallRange);
    range('warmth', warmthRange);
    range('fit', fitRange);
    if (query.trim()) parts.push(`search "${query.trim()}"`);
    if (!showActive) parts.push('active contracts hidden');
    return parts.length ? parts.join(' · ') : 'no filters';
  };

  const runExport = async () => {
    setExportState('working');
    const stamp = new Date();
    const day = stamp.toISOString().slice(0, 10);
    try {
      await exportBuildingsToExcel({
        scoreRgb,
        heading: showCustomOnly ? 'Hudson Building Index — custom list' : 'Hudson Building Index',
        summary: `${filtered.length.toLocaleString()} buildings · ${describeFilters()} · exported ${stamp.toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' })}`,
        fileName: `hudson-building-index${showCustomOnly ? '-custom-list' : ''}-${day}.xlsx`,
        rows: filtered.map((building) => {
          const company = companyLabel(building.company);
          const rule = knownCompany(building.company);
          const place = building.place;
          return {
            logo: logoFor(company),
            company,
            corp: corpLabel(building),
            address: place ? place.street.replace(/,\s*$/, '') : (building.address || ''),
            city: cityLabel(building.city),
            overall: building.rank.overall,
            warmth: building.rank.warmth,
            fit: building.rank.fit,
            managers: building.managerList,
            age: building.age == null ? null : Number(building.age),
            units: building.units == null ? null : Number(building.units),
            board: building.boardList,
            status: building.rank.client ? 'Active contract' : building.rank.terminated ? 'Lost' : '',
            connection: rule ? (rule.label || 'Weak connection') : 'No connection yet',
            km: building.rank.km == null ? null : Math.round(building.rank.km * 10) / 10,
            warmthWhy: building.rank.reasons.join(' · '),
            fitWhy: building.rank.fitNotes.join(' · '),
            updated: building.updated || '',
            registry: registryUrl(building.guid),
          };
        }),
      });
      setExportState('');
    } catch (error) {
      setExportState('error');
    }
  };

  const pickAllShown = (add) => {
    setCustomIds((current) => {
      const next = new Set(current);
      filtered.forEach((building) => {
        const id = buildingId(building);
        if (add) next.add(id);
        else next.delete(id);
      });
      return next;
    });
  };

  const changedCount = useMemo(() => ranked.reduce((total, building) => total + (building.change ? 1 : 0), 0), [ranked]);

  const askForCheck = async () => {
    const token = localStorage.getItem('managerToken');
    if (!token) return;
    setRefreshAsk('working');
    try {
      const response = await fetch(`${INDEX_API}/api/discovery/refresh`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      setRefreshAsk(response.ok ? 'asked' : 'error');
    } catch (error) {
      setRefreshAsk('error');
    }
  };

  const selectedId = selected ? buildingId(selected) : '';
  const advancedCount = (cities.length ? 1 : 0) + (maxKm !== '' ? 1 : 0) + (connection !== '' ? 1 : 0);
  const rangeTouched = (range) => range[0] > SCORE_MIN || range[1] < SCORE_MAX;
  const filtersActive = companies.length > 0 || cities.length > 0 || ageMin !== '' || maxKm !== '' || connection !== ''
    || rangeTouched(overallRange) || rangeTouched(warmthRange) || rangeTouched(fitRange) || query.trim() !== ''
    || !showActive || showCustomOnly || showChangedOnly;

  const clearFilters = () => {
    setCompanies([]);
    setCities([]);
    setAgeMin('');
    setMaxKm('');
    setConnection('');
    setOverallRange([SCORE_MIN, SCORE_MAX]);
    setWarmthRange([SCORE_MIN, SCORE_MAX]);
    setFitRange([SCORE_MIN, SCORE_MAX]);
    setQuery('');
    setShowActive(true);
    setShowCustomOnly(false);
    setShowChangedOnly(false);
  };

  return (
    <div className="building-index">
      <header className="building-index__header">
        <div>
          <h1>Building Index</h1>
          <p>
            {loadState === 'loading' && 'Loading buildings from the server...'}
            {loadState === 'error' && buildings.length === 0 && 'The building index could not be loaded from the server.'}
            {loadState === 'ready' && `${progress ? progress.hits : buildings.length} corporations indexed. ${progress?.label || ''}`}
          </p>
          {refresh && (
            <p className="building-index__watch">
              <span>{describeWeeklyCheck(refresh)}</span>
              <button type="button" onClick={askForCheck} disabled={refreshAsk === 'working' || refresh.running}>
                {refreshAsk === 'asked' ? 'Starting shortly' : refreshAsk === 'error' ? 'Could not start it' : 'Check now'}
              </button>
            </p>
          )}
        </div>
      </header>

      <label className="building-index__search">
        <span>Search</span>
        <input
          type="search"
          placeholder="Manager, address, corporation number, management company..."
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
      </label>

      <div className="building-index__filters">
        <label>
          <span>Management company</span>
          <Select
            isMulti
            options={companyOptions}
            value={companies}
            onChange={(value) => setCompanies(value || [])}
            placeholder="All companies"
            styles={selectStyles}
          />
        </label>
        <label className="building-index__age">
          <span>Minimum age</span>
          <input
            type="number"
            min="0"
            inputMode="numeric"
            placeholder="Years"
            value={ageMin}
            onChange={(event) => setAgeMin(event.target.value)}
          />
        </label>
        <div className="building-index__ranges">
          <RangeFilter label="Overall" value={overallRange} onChange={setOverallRange} />
          <RangeFilter label="Warmth" value={warmthRange} onChange={setWarmthRange} />
          <RangeFilter label="Fit" value={fitRange} onChange={setFitRange} />
          <button
            type="button"
            className={`building-index__chip${advancedCount ? ' building-index__chip--on' : ''}`}
            onClick={() => setAdvancedOpen((current) => !current)}
            aria-expanded={advancedOpen}
          >
            Advanced
            {advancedCount ? <span aria-hidden>{advancedCount}</span> : <span aria-hidden>{advancedOpen ? '▴' : '▾'}</span>}
          </button>
          <button
            type="button"
            className={`building-index__chip${showActive ? ' building-index__chip--on' : ''}`}
            onClick={() => setShowActive((current) => !current)}
            aria-pressed={showActive}
            title={showActive ? 'Hide buildings Hudson already has' : 'Show buildings Hudson already has'}
          >
            Active contracts
            <span aria-hidden>{showActive ? '✓' : '✕'}</span>
          </button>
          {(changedCount > 0 || runInfo || showChangedOnly) && (
            <button
              type="button"
              className={`building-index__chip${showChangedOnly ? ' building-index__chip--on' : ''}`}
              onClick={() => setShowChangedOnly((current) => !current)}
              aria-pressed={showChangedOnly}
              title={historyMode === 'latest' ? 'Buildings changed during the latest completed weekly collection' : `Buildings whose registry record moved in the last ${CHANGE_WINDOW_DAYS} days`}
            >
              Changed recently
              <span aria-hidden>{changedCount}</span>
            </button>
          )}
          {customIds.size > 0 && (
            <button
              type="button"
              className={`building-index__chip${showCustomOnly ? ' building-index__chip--on' : ''}`}
              onClick={() => setShowCustomOnly((current) => !current)}
              aria-pressed={showCustomOnly}
              title={showCustomOnly ? 'Show every building again' : 'Show only the buildings you picked'}
            >
              Custom list
              <span aria-hidden>{customIds.size}</span>
            </button>
          )}
          <button
            type="button"
            className="building-index__reset"
            onClick={clearFilters}
            disabled={!filtersActive}
          >
            Reset
          </button>
        </div>
      </div>

      {advancedOpen && (
        <div className="building-index__advanced">
          <label>
            <span>City</span>
            <Select
              isMulti
              options={cityOptions}
              value={cities}
              onChange={(value) => setCities(value || [])}
              placeholder="All cities"
              styles={selectStyles}
            />
          </label>
          <label className="building-index__distance">
            <span>Distance from office</span>
            <select value={maxKm} onChange={(event) => setMaxKm(event.target.value)}>
              <option value="">Any</option>
              {DISTANCE_OPTIONS.map((km) => (
                <option key={km} value={km}>{km} km</option>
              ))}
            </select>
          </label>
          <label className="building-index__connection">
            <span>Connection</span>
            <select value={connection} onChange={(event) => setConnection(event.target.value)}>
              <option value="">Any</option>
              <option value="1st">1st Degree</option>
            </select>
          </label>
        </div>
      )}

      {showChangedOnly && <BuildingCompanyChanges
        buildings={filtered}
        history={history}
        status={historyStatus}
        end={windowEnd}
        start={windowStart}
        mode={historyMode}
        onModeChange={setHistoryMode}
        runInfo={runInfo}
        onSelect={setSelected}
        onRetry={() => setHistoryRetry((value) => value + 1)}
      />}

      <div className="building-index__table-wrap">
        <div className="building-index__count">
          <span>
            {loadState === 'loading'
              ? 'Loading...'
              : filtered.length === 1
                ? '1 building'
                : `${filtered.length} buildings`}
          </span>
          <div className="building-index__actions">
            {exportState === 'error' && (
              <span className="building-index__action-note">The export could not be created.</span>
            )}
            <button
              type="button"
              className="building-index__bulk"
              onClick={() => pickAllShown(!showCustomOnly)}
              disabled={filtered.length === 0}
            >
              {showCustomOnly ? 'Remove all' : 'Add all to custom list'}
            </button>
            <button
              type="button"
              className="building-index__bulk building-index__bulk--solid"
              onClick={runExport}
              disabled={filtered.length === 0 || exportState === 'working'}
              title="Download these buildings as a formatted Excel file"
            >
              {exportState === 'working' ? 'Building the file...' : 'Export to Excel'}
            </button>
          </div>
        </div>
        <table>
          <colgroup>
            {SORT_COLUMNS.map((column) => (
              <col key={column.key} style={{ width: column.score ? SCORE_COLUMN_WIDTH : column.width }} />
            ))}
          </colgroup>
          <thead>
            <tr>
              {SORT_COLUMNS.map((column) => {
                if (column.action) return <th key={column.key} aria-label="Custom list" />;
                const active = sortKey === column.key;
                const ariaSort = active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none';
                return (
                  <th key={column.key} aria-sort={ariaSort} className={column.score ? 'building-index__score-col' : undefined}>
                    <button
                      type="button"
                      className={`building-index__sort${column.score ? ' building-index__sort--score' : ''}`}
                      onClick={() => toggleSort(column.key)}
                    >
                      {column.label}
                      <SortMark direction={active ? sortDir : ''} />
                    </button>
                    {column.tip && <span className="building-index__tip" role="tooltip">{column.tip}</span>}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loadState === 'loading' && (
              <tr>
                <td colSpan={SORT_COLUMNS.length} className="building-index__empty">
                  Loading buildings from the server...
                </td>
              </tr>
            )}
            {loadState !== 'loading' && filtered.length === 0 && (
              <tr>
                <td colSpan={SORT_COLUMNS.length} className="building-index__empty">
                  {loadState === 'error'
                    ? 'The building index could not be loaded from the server.'
                    : 'No buildings match these filters.'}
                </td>
              </tr>
            )}
            {visibleBuildings.map((building) => (
              <BuildingRow
                key={`${building.guid || ''}-${corpLabel(building)}-${building.address || ''}`}
                building={building}
                selected={selectedId === buildingId(building)}
                picked={customIds.has(buildingId(building))}
                onSelect={setSelected}
                onPick={togglePick}
              />
            ))}
          </tbody>
        </table>
        {loadState === 'ready' && visibleCount < filtered.length && (
          <div ref={sentinelRef} className="building-index__more" />
        )}
      </div>
      {selected && (
        <BuildingDetail building={selected} onClose={() => setSelected(null)} onOpen={setSelected} />
      )}
    </div>
  );
}

function BuildingDetail({ building, onClose, onOpen }) {
  const company = companyLabel(building.company);
  const logo = logoFor(company);
  const managers = splitNames(building.managers);
  const board = splitNames(building.board);
  const place = addressLines(building.address, building.city);
  const caoUrl = registryUrl(building.guid);
  const { rank } = building;
  const extraSiblings = Math.max(0, rank.siblingCount - rank.siblings.length);

  return createPortal(
    <div className="building-index__overlay" role="presentation" onClick={onClose}>
      <div
        className="building-index__dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="building-index-title"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="building-index__dialog-head">
          <div className="building-index__dialog-company">
            {logo ? <img src={logo} alt="" /> : null}
            <span>{company}</span>
          </div>
          <button type="button" className="building-index__close" onClick={onClose} aria-label="Close">×</button>
          <h2 id="building-index-title">
            {place ? place.street.replace(/,\s*$/, '') : (building.address || 'Building')}
            <span>{corpLabel(building)}</span>
            {rank.client && <em className="building-index__badge building-index__badge--client">Active contract</em>}
            {rank.terminated && <em className="building-index__badge building-index__badge--lost">Lost</em>}
          </h2>
          {place?.rest ? <p className="building-index__dialog-locality">{place.rest}</p> : null}
        </header>

        <div className="building-index__dialog-body">
          <div className="building-index__dialog-scores">
            <div>
              <span className="building-index__score-label">Overall</span>
              <ScoreCell value={rank.overall} size="big" />
            </div>
            <div>
              <span className="building-index__score-label">Warmth</span>
              <ScoreCell value={rank.warmth} size="big" />
            </div>
            <div>
              <span className="building-index__score-label">Fit</span>
              <ScoreCell value={rank.fit} size="big" />
            </div>
          </div>

          {building.change && (
            <section className="building-index__block">
              <h3>{`Changed in the registry · ${building.change.when}`}</h3>
              <ul className="building-index__changes">
                {building.change.lines.map((line) => (
                  <li key={line.field}>
                    <span>{line.label}</span>
                    <em>{line.was || '—'}</em>
                    <strong>{line.now || '—'}</strong>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {rank.reasons.length > 0 && (
            <section className="building-index__block">
              <h3>Why it ranks here</h3>
              <ul className="building-index__reasons">
                {rank.reasons.map((reason) => <li key={reason}>{reason}</li>)}
              </ul>
            </section>
          )}

          {rank.fitNotes.length > 0 && (
            <section className="building-index__block">
              <h3>The building</h3>
              <ul className="building-index__reasons building-index__reasons--plain">
                {rank.fitNotes.map((note) => <li key={note}>{note}</li>)}
              </ul>
            </section>
          )}

          <dl className="building-index__facts">
            <div>
              <dt>Manager</dt>
              <dd>{managers.length ? managers.join(', ') : '—'}</dd>
            </div>
            <div>
              <dt>Age</dt>
              <dd>{building.age == null ? '—' : `${building.age} years`}</dd>
            </div>
            <div>
              <dt>Voting units</dt>
              <dd>{building.units == null ? '—' : building.units.toLocaleString()}</dd>
            </div>
            <div>
              <dt>Distance from office</dt>
              <dd>{rank.km == null ? '—' : `${formatKm(rank.km)} km`}</dd>
            </div>
            <div>
              <dt>City</dt>
              <dd>{cityLabel(building.city)}</dd>
            </div>
            <div>
              <dt>Registry updated</dt>
              <dd>{building.updated || 'Not listed'}</dd>
            </div>
            <div className="building-index__facts-wide">
              <dt>Board</dt>
              <dd>{board.length ? board.join(', ') : '—'}</dd>
            </div>
          </dl>

          {caoUrl && (
            <a className="building-index__registry" href={caoUrl} target="_blank" rel="noopener noreferrer">
              <img src="https://www.condoauthorityontario.ca/wp-content/uploads/2023/02/logo.svg" alt="" />
              Open on the Condo Authority registry
            </a>
          )}

          {rank.siblings.length > 0 && (
            <section className="building-index__siblings">
              <h3>Other buildings with this manager</h3>
              <ul>
                {rank.siblings.map((sibling) => {
                  const siblingPlace = addressLines(sibling.address, sibling.city);
                  return (
                    <li key={buildingId(sibling)}>
                      <button type="button" onClick={() => onOpen(sibling)}>
                        <span>{corpLabel(sibling)}</span>
                        <strong>{siblingPlace ? siblingPlace.street.replace(/,\s*$/, '') : sibling.address}</strong>
                        <ScoreCell value={sibling.rank.overall} />
                      </button>
                    </li>
                  );
                })}
              </ul>
              {extraSiblings > 0 && <p>{extraSiblings} more {extraSiblings === 1 ? 'building' : 'buildings'} with this manager</p>}
            </section>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
